/**
 * ClientReleaseEntry — client view of a Client Hub release from AlderCAD (Matt, 2026-10-22;
 * api/_clientRelease.js, docs/CONSULTANT_BACKGROUNDS.md "Client Hub releases"). One card holds
 * what was released together: the plan set (the JourneyBook cover opening the flip viewer, with a
 * download), "Walk the model" on the same frozen version (/walk?hub=<walkToken>), and the list of
 * drawings, each viewable on its own. Placed by phase and date like a sketch ribbon.
 */
import { useState } from 'react'
import Markdown from '../../components/Markdown.jsx'
import { J, fmtJourneyDate } from '../journeyUI.jsx'
import JourneyBook from './JourneyBook.jsx'
import PdfFlipViewer from '../../components/PdfFlipViewer.jsx'

export default function ClientReleaseEntry({ entry, token }) {
  const d = entry.data || {}
  const drawings = Array.isArray(d.drawings) ? d.drawings : []
  const [viewing, setViewing] = useState(null)   // { url, title }
  // &j= tells the walk page who is walking, so the project channel hears when they go in and out
  const walkHref = d.walkToken ? '/walk?hub=' + encodeURIComponent(d.walkToken) + (token ? '&j=' + encodeURIComponent(token) : '') : null
  const dateStr = fmtJourneyDate(d.entryDate)
  const ready = d.status === 'released' && d.set?.url

  return (
    <div>
      <div style={{ textAlign: 'center', paddingTop: 10 }}>
        <div style={{ ...J.eyebrow, marginBottom: 18 }}>Plans &amp; 3D Model</div>
        <h2 style={{ ...J.title, maxWidth: 460, margin: '0 auto' }}>{entry.title || 'Plans and 3D model'}</h2>
        {dateStr && <div style={{ ...J.mono, marginTop: 10 }}>{dateStr}</div>}
        <div style={{ width: 36, height: 1, background: 'rgba(7,39,45,0.24)', margin: '26px auto 26px' }} />
      </div>
      {d.intro && <div style={{ ...J.body, maxWidth: 520, margin: '0 auto 26px' }}><Markdown text={d.intro} /></div>}

      {!ready ? (
        <div style={{ ...J.body, fontStyle: 'italic', color: '#8ba6b1', textAlign: 'center', padding: '18px 0 8px' }}>This is being prepared.</div>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: 36, padding: '6px 0 10px' }}>
            <JourneyBook label="Plans" aspect={17 / 11} pageCount={drawings.length}
              onOpen={() => setViewing({ url: d.set.url, title: entry.title || 'Plans' })}
              downloadUrl={d.set.downloadUrl || d.set.url} />
            {walkHref && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 230, textAlign: 'center' }}>
                <a href={walkHref} target="_blank" rel="noopener noreferrer" style={cta}>Walk the model</a>
                <div style={{ ...J.body, fontSize: 12.5, color: '#7a9aaa', marginTop: 12 }}>
                  The 3D model exactly as these plans show it. Turn and zoom around the outside, then step inside and walk through. Works on a phone or tablet too.
                </div>
              </div>
            )}
          </div>

          {drawings.length > 0 && (
            <div style={{ maxWidth: 520, margin: '30px auto 0' }}>
              <div style={{ ...J.eyebrow, marginBottom: 10 }}>In this release</div>
              {drawings.map((x, i) => (
                <button key={i} type="button" onClick={() => setViewing({ url: x.url, title: x.name })}
                  style={row}>
                  <span style={{ ...J.mono, width: 28, flexShrink: 0, textAlign: 'left' }}>{String(x.n || i + 1).padStart(2, '0')}</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>{x.name}</span>
                  <span style={{ ...J.mono, color: '#6b8f95' }}>View</span>
                </button>
              ))}
            </div>
          )}
          {viewing && <PdfFlipViewer pdfUrl={viewing.url} title={viewing.title} landscape onClose={() => setViewing(null)} />}
        </>
      )}
    </div>
  )
}

const cta = { display: 'inline-block', fontFamily: '"DM Sans", sans-serif', fontSize: 14, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 6, padding: '12px 26px', cursor: 'pointer', textDecoration: 'none' }
const row = {
  display: 'flex', alignItems: 'baseline', gap: 10, width: '100%', padding: '10px 2px', background: 'none', border: 'none',
  borderBottom: '0.5px solid rgba(7,39,45,0.14)', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif', fontSize: 14, color: '#07272D',
}
